import Link from 'next/link';
import { ArrowRight, Award, ExternalLink, Send, type LucideIcon } from 'lucide-react';
import { PendingBadge } from '@/components/admin/PendingBadge';
import { getSistema } from '@/lib/sistemas';

/**
 * Tarjeta de atajo de la última banda de la portada. La comparten las tres
 * caras que puede tener esa banda —perfil completo, panel de gestión y cuenta
 * sin ficha—, y por eso vive aparte: son la misma tarjeta, no tres parecidas.
 */
export interface HomeAtajo {
	label: string;
	href: string;
	icon: LucideIcon;
	/** Enlace fuera de la app: se abre en pestaña nueva y cambia la flecha. */
	externo?: boolean;
	/** Pendientes por revisar. `0` o ausente no pinta nada (ver `PendingBadge`). */
	pendientes?: number;
}

const CLASE =
	'group flex items-center gap-3 rounded-[10px] border border-slate-200 bg-white p-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#233a4d]/40';

export function HomeAtajoCard({ label, href, icon: Icon, externo = false, pendientes }: HomeAtajo) {
	const Flecha = externo ? ExternalLink : ArrowRight;

	const cuerpo = (
		<>
			<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F5F6FA] text-[#354857]">
				<Icon size={17} strokeWidth={2} aria-hidden />
			</span>
			<span className="min-w-0 flex-1 text-[13.5px] font-semibold text-slate-800">{label}</span>
			<PendingBadge count={pendientes ?? 0} aria-label={`${pendientes ?? 0} pendientes en ${label}`} />
			<Flecha
				size={14}
				aria-hidden
				className="shrink-0 text-slate-400 transition group-hover:text-[#354857]"
			/>
		</>
	);

	if (externo) {
		return (
			<a href={href} target="_blank" rel="noreferrer" className={CLASE}>
				{cuerpo}
			</a>
		);
	}

	return (
		<Link href={href} className={CLASE}>
			{cuerpo}
		</Link>
	);
}

/**
 * Los dos sistemas hermanos, que son el atajo por defecto de esta banda: los
 * ofrece tanto una ficha ya completa como una cuenta de gestión sin ficha.
 *
 * Salen del MISMO catálogo que el lobby (`lib/sistemas.ts`): su destino es un
 * build arg (`NEXT_PUBLIC_*_URL`). Sin la env el atajo no se pinta, en vez de
 * enlazar a la nada.
 */
export const ATAJOS_SISTEMAS: HomeAtajo[] = [
	{ label: 'Sistema de Incentivos', href: getSistema('incentivos')?.url ?? '', icon: Award, externo: true },
	{ label: 'Sistema de Postulaciones', href: getSistema('postulaciones')?.url ?? '', icon: Send, externo: true },
].filter((atajo) => Boolean(atajo.href));
